import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import { AppConstants } from '@core/constants/app';
import { ApplicationProgressResponse } from '@core/interfaces/api-responses/application-progress-response';
import { ApplicationService } from '@core/data/application.service';
import { UtilService } from '@core/data/util.service';
import { take } from 'rxjs/operators';
import { ApplicationConfigService } from '@core/data/application-config.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent {
  dashboardForm: FormGroup;
  translationPrefix = 'pages.dashboard';

  private static getFormConfig(params: { effectiveDateCutOffMonths: number }) {
    return {
      requestedEffectiveDate: [
        null,
        { validators: [Validators.required, DashboardComponent.validateDateLogic(params.effectiveDateCutOffMonths)], updateOn: 'blur' },
      ]
    };
  }

  private static validateDateLogic(effectiveDateCutOffMonths: number) {
    return (formControl: AbstractControl) => {
      if (!formControl.value || !formControl.value.length || formControl.value.length !== 10) {
        return null;
      }
      const value = moment(formControl.value, 'MM/DD/YYYY').startOf('day');

      if (!value.isValid()) {
        return { invalidDate: { valid: false } };
      }

      if (value.date() !== 1) {
        return { firstDayOfMonth: { valid: false } };
      }
      const start = moment().startOf('month').add(1, 'month');
      const end = start.clone().add((effectiveDateCutOffMonths <= 0 ? 0 : effectiveDateCutOffMonths - 1), 'months').endOf('day');
      return !value.isBetween(start, end, null, '[]') ? {
        dateRange: {
          valid: false,
          min: start.format('MM/DD/YYYY'),
          max: end.format('MM/DD/YYYY'),
        },
      } : null;
    };
  }

  constructor(
    private router: Router,
    private utilService: UtilService,
    private applicationService: ApplicationService,
    private fb: FormBuilder,
    private applicationConfigService: ApplicationConfigService
  ) {
    this.applicationConfigService.clearConfig();
    this.utilService.getMedicareEnrollmentConfig()
      .pipe(take(1))
      .subscribe(response => {
        this.dashboardForm = this.fb.group(DashboardComponent.getFormConfig({
          effectiveDateCutOffMonths: +response.EFFECTIVE_DATE_CUTOFF_MONTHS,
        }));
      });
  }

  get requestedEffectiveDate() {
    return this.dashboardForm.get('requestedEffectiveDate');
  }

  resolveApplicationYear() {
    if (this.dashboardForm.invalid) {
      return;
    }
    const formattedEffectiveDate = moment(this.requestedEffectiveDate.value, 'MM/DD/YYYY').format('YYYY-MM-DD');
    this.applicationService.resolveApplicationYear(formattedEffectiveDate)
      .pipe(take(1))
      .subscribe((response: ApplicationProgressResponse) => {
        this.applicationConfigService.setConfig(null, formattedEffectiveDate);
        if (response?.status === AppConstants.APP_STATUS_SUBMITTED) {
          this.router.navigate([AppConstants.CONFIRMATION_PAGE, response.id]);
          return;
        }
        this.router.navigate([AppConstants.INTRO_PAGE], {
          state: {
            requestedEffectiveDate: formattedEffectiveDate,
            application: response,
          },
        });
      });
  }
}
